import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import styled from "styled-components";

import {
  AuthWrapper,
  FormWrapper,
  ContentLogin,
  ImageWrapper,
  Form,
  ConfirmWrapp,
  LinkWrap,
  EmptyColumn,
  FiedlWrapper,
  ErrorMessage,
} from "../../style/Auth";

import logo from "../../images/ic_logo.svg";

import TextInput from "../../components/inputs/TextInput";
import PasswordInput from "../../components/inputs/PasswordInput";
import Button from "../../components/button/index";

import { registerPlayer, resendVerification, fetchReferralPreview } from "./api";
import { getStoredReferral, clearStoredReferral, captureReferralFromUrl } from "../../helpers/referral";
import { useLexicon } from "../../hooks/useLexicon";

// Substitute {placeholders} in a lexicon template with React nodes, in order,
// preserving per-language word order for bold/link/dynamic bits.
const interp = (tmpl, nodes) => {
  const parts = String(tmpl).split(/\{[^}]+\}/);
  return parts.flatMap((p, i) => (i < nodes.length ? [p, nodes[i]] : [p]));
};

// /player/register — self-serve player signup. Mirrors the Login page chrome
// (AuthWrapper + right-side hero image) so the auth surface feels consistent
// across login / register / verify-email. Distinct from the legacy
// /register page, which is the scout/manager onboarding form.
const PlayerRegister = () => {
  const navigate = useNavigate();
  const l = useLexicon();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Post-submit "check your email" state
  const [submittedEmail, setSubmittedEmail] = useState(null);
  const [resending, setResending] = useState(false);
  const [resentAt, setResentAt] = useState(null);

  // Referral: if the visitor arrived via a referral link, show "X invited you"
  // (referrer's first name only — public preview, GDPR-conscious).
  const [invitedBy, setInvitedBy] = useState(null);

  useEffect(() => {
    // Capture ?ref= from the URL here too: on a direct hit to
    // /player/register?ref=CODE this child effect runs before App's global
    // capture effect, so relying on localStorage alone would miss the banner.
    const code = captureReferralFromUrl(window.location.search) || getStoredReferral();
    if (!code) return;
    let alive = true;
    fetchReferralPreview(code)
      .then((res) => {
        const name = res?.data?.referrer?.first_name;
        if (alive && name) setInvitedBy(name);
      })
      .catch(() => {
        /* unknown code → no banner, attribution still attempted on submit */
      });
    return () => {
      alive = false;
    };
  }, []);

  const handleSubmit = async (event) => {
    event?.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const refCode = getStoredReferral();
      await registerPlayer({
        email: email.trim().toLowerCase(),
        password,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        ...(refCode ? { ref_code: refCode } : {}),
      });
      clearStoredReferral();
      setSubmittedEmail(email.trim().toLowerCase());
    } catch (err) {
      const code = err?.response?.data?.code;
      if (code === "EMAIL_ALREADY_REGISTERED") {
        setError(l(1747));
      } else if (err?.response?.status === 503) {
        setError(l(1748));
      } else {
        setError(err?.response?.data?.error || l(1749));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!submittedEmail) return;
    setResending(true);
    try {
      await resendVerification(submittedEmail);
    } catch {
      // Endpoint always 200s; UI shows confirmation regardless.
    } finally {
      setResentAt(new Date());
      setResending(false);
    }
  };

  if (submittedEmail) {
    return (
      <AuthWrapper>
        <FormWrapper>
          <ContentLogin>
            <EmptyColumn />
            <Form as="div">
              <a href="/">
                <img className="logo" src={logo} alt="Logo" />
              </a>
              <h1>{l(1728)}</h1>
              <p>
                {interp(l(1751), [
                  <strong key="e">{submittedEmail}</strong>,
                  <strong key="t">{l(1729)}</strong>,
                ])}
              </p>

              <ConfirmWrapp>
                <Button
                  title={resending ? l(1731) : l(1730)}
                  size="full"
                  sizeResponse="full"
                  type="button"
                  onClick={handleResend}
                  disabled={resending}
                />
                {resentAt && (
                  <ResentNote>
                    {interp(l(1750), [resentAt.toLocaleTimeString()])}
                  </ResentNote>
                )}
              </ConfirmWrapp>
            </Form>
            <LinkWrap>
              <h3>
                {l(1732)}{" "}
                <ResetLink onClick={() => setSubmittedEmail(null)}>
                  {l(1733)}
                </ResetLink>
              </h3>
            </LinkWrap>
          </ContentLogin>
        </FormWrapper>
        <ImageWrapper />
      </AuthWrapper>
    );
  }

  return (
    <AuthWrapper>
      <FormWrapper>
        <ContentLogin>
          <EmptyColumn />
          <Form onSubmit={handleSubmit}>
            <a href="/">
              <img className="logo" src={logo} alt="Logo" />
            </a>
            <h1>{l(1734)}</h1>
            {invitedBy && (
              <InviteBanner>
                {interp(l(1884), [<strong key="n">{invitedBy}</strong>])}
              </InviteBanner>
            )}
            <p>{interp(l(1752), [<strong key="t">{l(1729)}</strong>])}</p>

            <TextInput
              label={l(1735)}
              type="text"
              value={firstName}
              placeholder={l(1736)}
              onChange={setFirstName}
            />

            <TextInput
              label={l(1737)}
              type="text"
              value={lastName}
              placeholder={l(1738)}
              onChange={setLastName}
            />

            <TextInput
              label={l(1739)}
              type="email"
              value={email}
              placeholder="you@example.com"
              onChange={setEmail}
            />

            <FiedlWrapper>
              <PasswordInput
                label={l(322)}
                placeholder={l(1740)}
                value={password}
                onChange={setPassword}
              />
            </FiedlWrapper>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <ConfirmWrapp>
              <Button
                title={submitting ? l(1741) : l(1742)}
                size="full"
                sizeResponse="full"
                type="submit"
                disabled={submitting}
              />
              <Disclaimer>
                {interp(l(1753), [
                  <a key="t" href="https://smrtstats.com/terms" target="_blank" rel="noreferrer">{l(1743)}</a>,
                  <a key="p" href="https://smrtstats.com/privacy" target="_blank" rel="noreferrer">{l(1744)}</a>,
                ])}
              </Disclaimer>
            </ConfirmWrapp>
          </Form>
          <LinkWrap>
            <h3>
              {l(1745)}{" "}
              <Link to="/login">{l(1746)}</Link>
            </h3>
          </LinkWrap>
        </ContentLogin>
      </FormWrapper>
      <ImageWrapper />
    </AuthWrapper>
  );
};

export default PlayerRegister;

const Disclaimer = styled.p`
  /* Override the .Form p style which is loud (20px). The ToS micro-copy
     should sit quietly under the submit button. */
  &&& {
    color: ${(props) => props.theme.blueish2};
    font-family: "SatoshiRegular", sans-serif;
    font-size: 12px;
    line-height: 18px;
    margin: 12px 0 0;
    a {
      font-size: 12px;
      line-height: 18px;
      color: ${(props) => props.theme.blueish2};
      text-decoration: underline;
    }
  }
`;

const InviteBanner = styled.div`
  margin: 4px 0 12px;
  padding: 10px 14px;
  border-radius: 8px;
  background: rgba(46, 204, 113, 0.12);
  border: 1px solid ${(props) => props.theme.green};
  color: ${(props) => props.theme.white};
  font-family: "SatoshiRegular", sans-serif;
  font-size: 13px;
  line-height: 18px;

  strong {
    font-family: "SatoshiBold", sans-serif;
    color: ${(props) => props.theme.green};
  }
`;

const ResentNote = styled.span`
  display: inline-block;
  margin-top: 10px;
  color: ${(props) => props.theme.blueish2};
  font-family: "SatoshiRegular", sans-serif;
  font-size: 12px;
`;

const ResetLink = styled.button`
  background: transparent;
  border: 0;
  padding: 0;
  color: ${(props) => props.theme.green};
  font-family: "SatoshiRegular", sans-serif;
  font-size: inherit;
  text-decoration: underline;
  cursor: pointer;
`;
